import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Loader2,
  Maximize2,
  Minimize2,
  Volume2,
  VolumeX,
  Play,
  Pause,
  ExternalLink,
  Globe,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface DigitalResourceViewerProps {
  fileUrl: string;
  type: string;
  mimeType?: string;
  title: string;
  /** When true, show open-website UI instead of in-app file viewers. */
  isExternalUrl?: boolean;
  onError?: (error: string) => void;
}

export function DigitalResourceViewer({
  fileUrl,
  type,
  mimeType,
  title,
  isExternalUrl,
  onError,
}: DigitalResourceViewerProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const audioRef = React.useRef<HTMLAudioElement>(null);

  useEffect(() => {
    // Handle fullscreen changes
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // External website / online-only resources (do not treat signed storage URLs as external)
  if (isExternalUrl || type === "WEBSITE") {
    return (
      <Card className="border border-gray-200">
        <CardContent className="flex flex-col items-center justify-center gap-4 py-16 px-6 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-50">
            <Globe className="h-8 w-8 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="mt-1 text-sm text-gray-500">
              This resource is available on an external website.
            </p>
          </div>
          <Button asChild className="gap-2">
            <a href={fileUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4" />
              Open Website
            </a>
          </Button>
          <p className="max-w-md break-all text-xs text-gray-400">{fileUrl}</p>
        </CardContent>
      </Card>
    );
  }

  const handleLoad = () => {
    setLoading(false);
    setError(null);
  };

  const handleError = (err: any) => {
    setLoading(false);
    const errorMsg = "Failed to load resource. Please try again later.";
    setError(errorMsg);
    onError?.(errorMsg);
  };

  const toggleFullscreen = async () => {
    const container = document.getElementById("viewer-container");
    if (!container) return;

    try {
      if (!document.fullscreenElement) {
        await container.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error("Fullscreen error:", err);
    }
  };

  const togglePlayPause = () => {
    if (type === "VIDEO" && videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    } else if (type === "AUDIO" && audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (type === "VIDEO" && videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    } else if (type === "AUDIO" && audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleTimeUpdate = () => {
    if (type === "VIDEO" && videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      setDuration(videoRef.current.duration);
    } else if (type === "AUDIO" && audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      setDuration(audioRef.current.duration);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // PDF Viewer
  if (type === "PDF" || mimeType?.includes("pdf")) {
    return (
      <div id="viewer-container" className="w-full h-full flex flex-col">
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <h3 className="font-semibold">{title}</h3>
          <Button variant="outline" size="sm" onClick={toggleFullscreen}>
            {isFullscreen ? (
              <>
                <Minimize2 className="h-4 w-4 mr-2" />
                Exit Fullscreen
              </>
            ) : (
              <>
                <Maximize2 className="h-4 w-4 mr-2" />
                Fullscreen
              </>
            )}
          </Button>
        </div>
        <div className="flex-1 relative bg-gray-100">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}
          {error ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="text-red-500">{error}</p>
            </div>
          ) : (
            <iframe
              src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=1`}
              className="w-full h-full border-0"
              title={title}
              onLoad={handleLoad}
              onError={handleError}
              style={{ minHeight: "600px" }}
            />
          )}
        </div>
      </div>
    );
  }

  // Video Viewer
  if (type === "VIDEO" || mimeType?.startsWith("video/")) {
    return (
      <div id="viewer-container" className="w-full h-full flex flex-col">
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <h3 className="font-semibold">{title}</h3>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={toggleMute}>
              {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </Button>
            <Button variant="outline" size="sm" onClick={toggleFullscreen}>
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        <div className="flex-1 relative bg-black">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
          )}
          {error ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="text-red-500">{error}</p>
            </div>
          ) : (
            <>
              <video
                ref={videoRef}
                src={fileUrl}
                className="w-full h-full"
                controls={false}
                onLoadedData={handleLoad}
                onError={handleError}
                onTimeUpdate={handleTimeUpdate}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
              />
              <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-4">
                <div className="flex items-center gap-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={togglePlayPause}
                    className="text-white hover:bg-white/20"
                  >
                    {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                  </Button>
                  <div className="flex-1 text-white text-sm">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Audio Viewer
  if (type === "AUDIO" || mimeType?.startsWith("audio/")) {
    return (
      <div id="viewer-container" className="w-full h-full flex flex-col">
        <Card className="flex-1">
          <CardContent className="p-8">
            <div className="flex flex-col items-center justify-center h-full space-y-6">
              <div className="text-center">
                <h3 className="text-2xl font-semibold mb-2">{title}</h3>
                <p className="text-gray-500">Audio Resource</p>
              </div>

              {loading && (
                <div className="flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              )}

              {error ? (
                <p className="text-red-500">{error}</p>
              ) : (
                <>
                  <div className="w-full max-w-md">
                    <audio
                      ref={audioRef}
                      src={fileUrl}
                      className="w-full"
                      controls
                      onLoadedData={handleLoad}
                      onError={handleError}
                      onTimeUpdate={handleTimeUpdate}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                      onEnded={() => setIsPlaying(false)}
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <Button variant="outline" size="sm" onClick={toggleMute}>
                      {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                    </Button>
                    <div className="text-sm text-gray-600">
                      {formatTime(currentTime)} / {formatTime(duration)}
                    </div>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // eBook Viewer (using iframe for EPUB, etc.)
  if (type === "EBOOK" || mimeType?.includes("epub")) {
    return (
      <div id="viewer-container" className="w-full h-full flex flex-col">
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <h3 className="font-semibold">{title}</h3>
          <Button variant="outline" size="sm" onClick={toggleFullscreen}>
            {isFullscreen ? (
              <>
                <Minimize2 className="h-4 w-4 mr-2" />
                Exit Fullscreen
              </>
            ) : (
              <>
                <Maximize2 className="h-4 w-4 mr-2" />
                Fullscreen
              </>
            )}
          </Button>
        </div>
        <div className="flex-1 relative bg-gray-100">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}
          {error ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="text-red-500">{error}</p>
            </div>
          ) : (
            <div className="w-full h-full p-8 overflow-auto">
              <div className="max-w-4xl mx-auto bg-white shadow-lg p-8 rounded-lg">
                <p className="text-gray-500 text-center mb-8">
                  eBook viewing requires a compatible reader. Please use the download option if
                  available, or view in a compatible e-reader application.
                </p>
                <iframe
                  src={fileUrl}
                  className="w-full border rounded"
                  style={{ minHeight: "600px" }}
                  title={title}
                  onLoad={handleLoad}
                  onError={handleError}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Generic file viewer (fallback)
  return (
    <div id="viewer-container" className="w-full h-full flex flex-col">
      <div className="flex items-center justify-between p-4 border-b bg-gray-50">
        <h3 className="font-semibold">{title}</h3>
        <Button variant="outline" size="sm" onClick={toggleFullscreen}>
          {isFullscreen ? (
            <>
              <Minimize2 className="h-4 w-4 mr-2" />
              Exit Fullscreen
            </>
          ) : (
            <>
              <Maximize2 className="h-4 w-4 mr-2" />
              Fullscreen
            </>
          )}
        </Button>
      </div>
      <div className="flex-1 relative bg-gray-100">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}
        {error ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-red-500">{error}</p>
          </div>
        ) : (
          <iframe
            src={fileUrl}
            className="w-full h-full border-0"
            title={title}
            onLoad={handleLoad}
            onError={handleError}
            style={{ minHeight: "600px" }}
          />
        )}
      </div>
    </div>
  );
}
